import clsx from "clsx";
import React, { FC } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  LightChevronLeftSvg,
  LightChevronRightSvg,
} from "@dndbeyond/character-components/es";

import { Link } from "~/components/Link";
import { RouteKey, SectionKey } from "~/subApps/builder/constants";

import { appEnvSelectors } from "../../../Shared/selectors";
import { navigationConfig } from "../../config";
import { RouteDef, SettingsLocationState } from "../../config/navigation";
import { builderSelectors } from "../../selectors";
import { getRoutePathIdx } from "../../utils/navigationUtils";

interface Props {
  characterId: number | null;
  pathname: string;
}

export const TodoNavigation: FC<Props> = ({ characterId, pathname }) => {
  const isMobile = useSelector(appEnvSelectors.getIsMobile);
  const builderMethod = useSelector(builderSelectors.getBuilderMethod);
  const isBuilderFlowExperimentEnabled = useSelector(
    builderSelectors.getIsBuilderFlowExperimentEnabled
  );
  const firstAvailableSectionRoutes = useSelector(
    builderSelectors.getFirstAvailableSectionRoutes
  );
  const { state: locationState } = useLocation();

  const availableRoutePath = useSelector(
    builderSelectors.getAvailableRoutePathing
  );

  const currentRouteDef = navigationConfig.getCurrentRouteDef(pathname);
  const currentRoutePathIdx = currentRouteDef
    ? getRoutePathIdx(currentRouteDef.key, availableRoutePath)
    : -1;

  const prevRouteDef: RouteDef | null =
    currentRoutePathIdx > 0
      ? availableRoutePath[currentRoutePathIdx - 1]
      : null;
  const nextRouteDef: RouteDef | null =
    currentRoutePathIdx >= 0
      ? availableRoutePath[currentRoutePathIdx + 1] ?? null
      : null;

  const isSettingsPage =
    isBuilderFlowExperimentEnabled &&
    currentRouteDef?.key === RouteKey.HOME_BASIC_INFO;

  // On the settings page, continue to the page the user came from, falling back to Class
  const getContinueTarget = (): {
    routeDef: RouteDef;
    sectionLabel: string;
  } | null => {
    const { returnTo } = (locationState ?? {}) as SettingsLocationState;
    const returnToRouteDef: RouteDef | null = returnTo
      ? navigationConfig.getCurrentRouteDef(returnTo)
      : null;
    const returnToAvailableRouteDef = returnToRouteDef
      ? availableRoutePath.find((route) => route.key === returnToRouteDef.key)
      : undefined;

    const sections = navigationConfig.getNavigationSections(
      builderMethod,
      returnToAvailableRouteDef ?? null,
      isBuilderFlowExperimentEnabled
    );
    const section = returnToAvailableRouteDef
      ? sections.find((navSection) => navSection.active)
      : sections.find((navSection) => navSection.key === SectionKey.CLASS);
    const routeDef =
      returnToAvailableRouteDef ??
      firstAvailableSectionRoutes[SectionKey.CLASS];

    if (!section || !routeDef) {
      return null;
    }

    return { routeDef, sectionLabel: section.getLabel() };
  };

  const handleNavClick = (): void => {
    window.scrollTo(0, 0);
  };

  const renderContinueUi = (): React.ReactNode => {
    const continueTarget = getContinueTarget();
    if (continueTarget === null) {
      return null;
    }

    const href = continueTarget.routeDef.path.replace(
      ":characterId",
      String(characterId)
    );

    if (isMobile) {
      return (
        <div className="builder-navigation">
          <Link
            className="builder-navigation-link"
            href={href}
            onClick={handleNavClick}
            userouter
          >
            Continue &gt;
          </Link>
        </div>
      );
    }

    return (
      <div className="builder-navigation-large">
        <Link
          className="builder-navigation-large-link"
          href={href}
          onClick={handleNavClick}
          userouter
        >
          <div
            className={clsx([
              "builder-navigation-large-action",
              "builder-navigation-large-action-next",
              "builder-navigation-large-action-continue",
            ])}
          >
            <div className="builder-navigation-large-action-text">Continue</div>
            <div className="builder-navigation-large-action-icon">
              <LightChevronRightSvg />
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const renderNonMobileUi = (): React.ReactNode => (
    <div className="builder-navigation-large">
      {prevRouteDef !== null && (
        <Link
          className="builder-navigation-large-link"
          href={prevRouteDef.path.replace(":characterId", String(characterId))}
          onClick={handleNavClick}
          userouter
        >
          <div
            className={clsx([
              "builder-navigation-large-action",
              "builder-navigation-large-action-prev",
            ])}
          >
            <div className="builder-navigation-large-action-icon">
              <LightChevronLeftSvg />
            </div>
            <div className="builder-navigation-large-action-text">Prev</div>
          </div>
        </Link>
      )}
      {nextRouteDef !== null && (
        <Link
          className="builder-navigation-large-link"
          href={nextRouteDef.path.replace(":characterId", String(characterId))}
          onClick={handleNavClick}
          userouter
        >
          <div
            className={clsx([
              "builder-navigation-large-action",
              "builder-navigation-large-action-next",
            ])}
          >
            <div className="builder-navigation-large-action-text">Next</div>
            <div className="builder-navigation-large-action-icon">
              <LightChevronRightSvg />
            </div>
          </div>
        </Link>
      )}
    </div>
  );

  const renderMobileUi = (): React.ReactNode => {
    return (
      <div className="builder-navigation">
        {prevRouteDef !== null ? (
          <Link
            className="builder-navigation-link"
            href={prevRouteDef.path.replace(
              ":characterId",
              String(characterId)
            )}
            onClick={handleNavClick}
            userouter
          >
            &lt; Prev
          </Link>
        ) : (
          <div className="builder-navigation-link builder-navigation-link-disabled">
            &lt; Prev
          </div>
        )}
        {nextRouteDef !== null ? (
          <Link
            className="builder-navigation-link"
            href={nextRouteDef.path.replace(
              ":characterId",
              String(characterId)
            )}
            onClick={handleNavClick}
            userouter
          >
            Next &gt;
          </Link>
        ) : (
          <div className="builder-navigation-link builder-navigation-link-disabled">
            Next &gt;
          </div>
        )}
      </div>
    );
  };

  if (isSettingsPage) {
    return <>{renderContinueUi()}</>;
  }

  return <>{isMobile ? renderMobileUi() : renderNonMobileUi()}</>;
};
